<template>
  <div id="beta-z-plots" class="ui grid"></div>
</template>

<script>
import { mapGetters } from "vuex";
import fieldDefinitions from "@/utils/fields";
import { stateTransitions } from "@/utils/transitions";

export default {
  name: "BetaZPlots",
  mounted() {
    this.renderBarPlots();
  },
  watch: {
    thisPatient() {
      this.renderBarPlots();
    }
  },
  computed: {
    ...mapGetters(["thisPatient", "patientData"])
  },
  data() {
    return {
      svg: null
    };
  },
  methods: {
    renderBarPlots() {
      const data = this.thisPatient;
      if (Object.keys(data).length) {
        // Remove previous plots if click calculate more than one times
        this.$d3.selectAll("#beta-z-plots .five.wide.column").remove();

        // Plot Chart per Transition
        Object.entries(stateTransitions).forEach(([key, transition]) => {
          const { title, percentage, total } = transition;
          this.renderBarPlot(
            data["betaZ"][key],
            `${title}&ensp;(n=${total})&nbsp;${percentage}`
          );
        });
      }
    },

    renderBarPlot(data, transition) {
      const positiveColor = "#f2994a88";
      const negativeColor = "#2e68bd88";

      // Clean Component Names
      data = data
        .filter(i => {
          if (i.comp in fieldDefinitions) {
            return i.comp in this.patientData;
          } else {
            return true;
          }
        })
        .map(i => {
          i.comp = i.comp
            .toUpperCase()
            .split("_")
            .join(" ");
          return i;
        });

      // Set the dimensions and margins of the graph
      const margin = { top: 30, right: 30, bottom: 50, left: 100 };
      const width = 400 - margin.left - margin.right;
      const height = 350 - margin.top - margin.bottom;

      // Append to template
      this.svg = this.$d3
        .select("#beta-z-plots")
        .append("div")
        .attr("class", "five wide column")
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", `translate(${margin.left}, ${margin.top})`);

      // Add X axis
      const minX = Math.min(
        this.$d3.min(data, d => parseFloat(d.BetaZ)),
        -0.1
      );
      const maxX = Math.max(
        this.$d3.max(data, d => parseFloat(d.BetaZ)),
        0.1
      );
      const x = this.$d3
        .scaleLinear()
        .domain([minX, maxX])
        .nice()
        .range([0, width]);

      this.svg
        .append("g")
        .attr("transform", `translate(0, ${height})`)
        .call(this.$d3.axisBottom(x).ticks(5))
        .selectAll("text")
        .attr("transform", "translate(-10,0) rotate(-45)")
        .style("text-anchor", "end");

      // Y axis
      const y = this.$d3
        .scaleBand()
        .range([0, height])
        .domain(data.map(d => d.comp))
        .padding(Math.max(0.8 - 0.1 * data.length, 0.1));

      this.svg.append("g").call(this.$d3.axisLeft(y));

      // Bars
      this.svg
        .selectAll("myRect")
        .data(data)
        .enter()
        .append("rect")
        .attr("class", d => `bar bar--${d.BetaZ < 0 ? "negative" : "positive"}`)
        .attr("x", d => x(Math.min(0, d.BetaZ)))
        .attr("y", d => y(d.comp))
        .attr("width", d => Math.abs(x(d.BetaZ) - x(0)))
        .attr("height", y.bandwidth())
        .attr("fill", d => (d.BetaZ < 0 ? negativeColor : positiveColor));

      // Title
      this.svg
        .append("text")
        .attr("x", width / 2 - 30)
        .attr("y", -15)
        .attr("class", "title")
        .attr("text-anchor", "middle")
        .html(transition);

      // Vertical line at 0
      this.svg
        .append("line")
        .attr("x1", x(0))
        .attr("y1", 0)
        .attr("x2", x(0))
        .attr("y2", height)
        .style("stroke-width", 2)
        .style("stroke", "black")
        .style("fill", "none");

      // X label
      this.svg
        .append("text")
        .attr(
          "transform",
          `translate(${width / 2},${height + margin.top + 15})`
        )
        .attr("text-anchor", "middle")
        .attr("font-size", "14px")
        .attr("class", "x-axis-label")
        .text("Low Transition Risk   →   High Transition Risk");
    }
  }
};
</script>

<style lang="scss">
#beta-z-plots {
  g.tick text {
    fill: #9fa3af;
    font-family: DM sans;
    font-size: 11px;
  }
  line,
  path {
    stroke: #9fa3af;
  }

  .five.wide.column {
    min-width: 400px;
    margin: auto;
    margin-top: 50px;
  }
  .title {
    fill: #00113d;
    font-family: DM Sans;
    font-size: 14px;
    font-weight: bold;
  }
  .x-axis-label {
    fill: #00113d;
    font-family: DM sans;
    font-size: 14px;
    text-anchor: middle;
  }
}
</style>
